import React from 'react';
import InvoicePanel from './InvoicePanel';
import PaymentActionsPanel from './PaymentActionsPanel';

export default function AppointmentPaymentSection({
  shouldSkipPayment,
  paymentsEnabled,
  mode,
  appointment,
  user,
  appointmentStatus,
  isPaid,
  isPaymentCheckboxDisabled,
  paymentStatus,
  paymentActionLoading,
  onIsPaidChange,
  invoiceFile,
  uploadingInvoice,
  invoiceJustUploaded,
  onInvoiceFileChange,
  onClearInvoiceFile,
  invoiceItemText,
  useAlternatePaymentPhone,
  alternatePaymentPhone,
  alternatePaymentPhoneError,
  maxPayments,
  payments,
  paymentLoading,
  paymentError,
  formErrors,
  onInvoiceItemTextChange,
  onToggleAlternatePhone,
  onAlternatePhoneChange,
  onAlternatePhoneBlur,
  onMaxPaymentsChange,
  onRequestPayment,
  onCancelPayment,
  onViewPayment,
}) {
  if (shouldSkipPayment || appointmentStatus === 'no_show_justified') return null;

  return (
    <>
      {/* Payment checkbox */}
      <div className="md:col-span-1">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          מצב התשלום
        </label>
        <div className="flex items-center">
          <input
            type="checkbox"
            name="is_paid"
            disabled={isPaymentCheckboxDisabled}
            checked={isPaid || paymentStatus === 'completed'}
            onChange={(e) => onIsPaidChange(e.target.checked)}
            className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded ml-1 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <label className="mr-1 text-sm font-medium text-gray-700">
            התקבל תשלום
          </label>
        </div>
        {isPaymentCheckboxDisabled && (
          <p className="mt-1 text-xs text-gray-500">
            {paymentActionLoading
              ? 'ממתין ליצירת קישור תשלום...'
              : paymentStatus === 'pending'
                ? 'יש בקשת תשלום פעילה. בטל אותה כדי לסמן תשלום ידני.'
                : 'לא ניתן לשנות סטטוס תשלום כאשר יש חשבונית או תשלום שהושלם.'
            }
          </p>
        )}
      </div>

      {/* Invoice upload for cash payments */}
      {isPaid && paymentStatus !== 'completed' && !appointment?.invoice_url && !invoiceJustUploaded && (
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            העלאת חשבונית ידנית (אופציונלי)
          </label>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => onInvoiceFileChange(e.target.files[0])}
                disabled={uploadingInvoice}
                className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100 disabled:opacity-50"
              />
              {invoiceFile && !uploadingInvoice && (
                <button
                  type="button"
                  onClick={onClearInvoiceFile}
                  className="text-red-600 hover:text-red-800 text-sm font-medium whitespace-nowrap"
                >
                  ביטול
                </button>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              ניתן להעלות קובץ PDF או תמונה של החשבונית
            </p>
            {invoiceFile && !uploadingInvoice && (
              <p className="mt-2 text-sm text-green-600">
                קובץ נבחר: {invoiceFile.name}
              </p>
            )}
            {uploadingInvoice && (
              <div className="mt-2 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-pink-600"></div>
                <p className="text-sm text-pink-600">
                  מעלה חשבונית...
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invoice upload success */}
      {isPaid && invoiceJustUploaded && (
        <div className="md:col-span-2">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-green-700 font-medium">
                החשבונית הועלתה בהצלחה
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment / Invoice panel — edit mode only */}
      {paymentsEnabled && mode === 'edit' && appointment && user && (
        <div className="md:col-span-2 mt-2">
          {appointment.invoice_url !== null ? (
            <InvoicePanel appointment={appointment} />
          ) : (
            <div className="border-2 border-pink-100 rounded-xl p-5 bg-gradient-to-br from-pink-50/30 to-purple-50/30 space-y-4">
              <div className="flex items-center gap-2 mb-2 pb-3 border-b border-pink-200/50">
                <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <h4 className="text-sm font-semibold text-gray-800">אפשרויות תשלום</h4>
              </div>

              {/* Paid in cash message */}
              {isPaid && paymentStatus === 'not_requested' && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-green-700 font-medium">שולם במזומן</span>
                  </div>
                </div>
              )}

              {/* Optional invoice item text */}
              {!appointment.is_paid && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    תוספת טקסט לחשבונית (אופציונלי)
                  </label>
                  <input
                    type="text"
                    value={invoiceItemText}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 100) {
                        onInvoiceItemTextChange(value);
                      }
                    }}
                    maxLength={100}
                    placeholder="טקסט שיופיע נוסף על שם השירות בחשבונית"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500 text-sm"
                    dir="auto"
                  />
                  <div className="flex items-start justify-between text-xs text-gray-500">
                    <span>{invoiceItemText.length}/100 תווים</span>
                  </div>
                  {invoiceItemText.trim() && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-xs text-gray-600 mb-1">תצוגה מקדימה לחשבונית:</p>
                      <p className="text-sm font-medium text-gray-900">
                        {appointment.service_name || 'שם השירות'} - {invoiceItemText}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Optional alternate payment phone */}
              {!appointment.is_paid && (
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={useAlternatePaymentPhone}
                      onChange={(e) => onToggleAlternatePhone(e.target.checked)}
                      className="h-4 ml-2 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      השתמש במספר טלפון שונה לבקשת תשלום
                    </span>
                  </label>

                  {useAlternatePaymentPhone && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={alternatePaymentPhone}
                        onChange={(e) => onAlternatePhoneChange(e.target.value)}
                        onBlur={onAlternatePhoneBlur}
                        placeholder="מספר טלפון לבקשת תשלום (למשל: 050-123-4567)"
                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500 text-sm ${alternatePaymentPhoneError ? 'border-red-300' : 'border-gray-300'
                          }`}
                        dir="ltr"
                      />
                      {alternatePaymentPhoneError && (
                        <p className="mt-1 text-sm text-red-600">{alternatePaymentPhoneError}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        קישור התשלום יישלח למספר זה במקום למספר הטלפון של הלקוח
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Payment installments */}
              {!appointment.is_paid && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    מספר תשלומים מקסימלי
                  </label>
                  <select
                    value={maxPayments}
                    onChange={(e) => onMaxPaymentsChange(Number(e.target.value))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-pink-500 focus:border-pink-500 text-sm"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                      <option key={num} value={num}>
                        {num === 1 ? 'תשלום אחד (ברירת מחדל)' : `עד ${num} תשלומים`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    {maxPayments === 1
                      ? 'הלקוח יוכל לשלם בתשלום אחד בלבד'
                      : `הלקוח יוכל לבחור בין תשלום אחד לבין ${maxPayments} תשלומים. הבחירה היא אופציונלית ללקוח.`
                    }
                  </p>
                </div>
              )}

              <PaymentActionsPanel
                appointment={appointment}
                activePayments={payments}
                onRequestPayment={onRequestPayment}
                onCancelPayment={onCancelPayment}
                onViewPayment={onViewPayment}
                loading={paymentActionLoading || paymentLoading}
              />
            </div>
          )}

          {(paymentError || formErrors.payment) && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {paymentError || formErrors.payment}
            </div>
          )}
        </div>
      )}
    </>
  );
}
